/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';

// Constants
import { queryKeys } from '@config/constants/query-keys';

// Lib
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';

// Types
import { TMeasurabilityProject } from '@global-types/api/api';

import { TApiResponseGetLatestProjects } from '../types';
import { TParamsGetApi } from '@global-types/index';
import { convertGCO2EToTCO2EWithoutUnitFloor } from '@utils/functions';
import { TParsedMesurabilityProject } from '@features/resources/measurabilities/types';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getLatestProjects = (
	params: Partial<TParamsGetApi>
): Promise<TApiResponseGetLatestProjects> => {
	return apiClient.get('/measurabilities', {
		params: {
			sort: params.sortParams,
			pageSize: params?.queryParams?.pageSize || 3,
			populate: 'cover, supportedBy.logo',
			active: true,
		},
	});
};

export const getLatestProjectsPublic = (
	params: Partial<TParamsGetApi>
): Promise<TApiResponseGetLatestProjects> => {
	return apiClient.get('/public/measurabilities', {
		params: {
			sort: params.sortParams,
			pageSize: params?.queryParams?.pageSize || 3,
			populate: 'cover, supportedBy.logo',
			active: true,
		},
	});
};

type UseLatestProjectsOptions = {
	queryConfig?: QueryConfig<typeof getLatestProjects>;
	queryParams: Record<string, any>;
	sortParams?: string;
};
/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */

export const useLatestProjectsQuery = ({
	queryConfig,
	queryParams,
	sortParams,
}: UseLatestProjectsOptions) => {
	const { checkRole } = useAuthorization();
	const latestProjectsQuery = useQuery({
		staleTime: 0,
		queryKey: [
			queryKeys.app.dashboard.latestProjects.key,
		],
		queryFn: () => {
			if (
				!checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				])
			) {
				return getLatestProjectsPublic({
					queryParams,
					sortParams,
				});
			}
			return getLatestProjects({
				queryParams,
				sortParams,
			});
		},
		...queryConfig,
	});

	const updateList = (
		projects: TMeasurabilityProject[]
	) =>
		projects.reduce(
			(
				acc: TMeasurabilityProject[],
				currentItem: TMeasurabilityProject
			) => {
				const modifiedItem: TParsedMesurabilityProject = {
					...currentItem,
					tokenAvailable:
						currentItem?.quantity &&
						currentItem?.quantity?.remaining_aco2
							? convertGCO2EToTCO2EWithoutUnitFloor(
									currentItem.quantity.remaining_aco2
								)
							: 0,
				};
				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

	return {
		...latestProjectsQuery,
		data: latestProjectsQuery?.data?.rows
			? updateList(latestProjectsQuery.data.rows)
			: [],
	};
};
